/**
 * 内容プレビューモーダル（成果物・ナレッジ共通の1UI）。
 *
 * 成果物（スレッドの「見る」）とナレッジ（一覧の「見る」）の両方で同じ見た目・同じ拡大幅で
 * 内容を表示する。前回 Phase1 で拡大した成果物プレビューをここに集約し流用する（1UI）。
 *
 * kind:
 *  - html      : 軽量 HTML（docx/xlsx/pptx 抽出）。previewHtml があれば srcDoc、無ければ src。
 *  - html-file : 生の HTML 成果物そのもの（変換なし・実物表示）。presigned URL を
 *                sandbox="allow-scripts" の iframe で表示する。allow-same-origin は
 *                **絶対に付けない**（AI 生成 HTML は信頼できないコンテンツ。opaque origin に
 *                することで、成果物内スクリプトからアプリのトークン・ストレージを遮断する。
 *                OCI 版は同一オリジンプロキシ配信のため、この sandbox が唯一の分離層になる）。
 *  - markdown  : presigned URL を取得して Markdown レンダリング
 *  - text      : presigned URL を取得して生テキスト表示（txt/json/csv）
 *  - image     : presigned URL を <img> 表示
 *  - pdf       : presigned URL を iframe（ブラウザ標準 PDF ビューア）で表示
 */
import { useEffect, useState } from "react"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

export interface PreviewItem {
  name: string
  previewKind?: string
  previewUrl?: string
  url?: string
  /** ResponseContentDisposition=attachment 付き presigned URL。
   *  cross-origin S3 では <a download> 属性が無視されるため、強制 DL 用に分けて持つ。
   *  存在しなければ url にフォールバック（後方互換）。 */
  downloadUrl?: string
  /** docx/xlsx/pptx をサーバ生成した軽量 HTML をインライン表示する場合（ナレッジ） */
  previewHtml?: string
}

export function PreviewModal({
  item,
  onClose,
}: {
  item: PreviewItem
  onClose: () => void
}) {
  const [text, setText] = useState<string>("")
  const [err, setErr] = useState("")
  const kind = item.previewKind
  const src = item.previewUrl || item.url || ""

  useEffect(() => {
    if ((kind === "markdown" || kind === "text") && src) {
      fetch(src)
        .then((r) => r.text())
        .then(setText)
        .catch((e) => setErr(String(e)))
    }
  }, [kind, src])

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      {/* 成果物・ナレッジ共通の拡大プレビュー。既定 sm:max-w-lg を sm: 込みで上書きし w-[95vw] で追従。 */}
      <DialogContent className="w-[95vw] max-w-7xl sm:max-w-7xl max-h-[92dvh] flex flex-col shadow-2xl">
        <DialogHeader>
          <DialogTitle className="pr-8 text-base">👁 {item.name}</DialogTitle>
          <p className="text-xs text-muted-foreground">
            {kind === "html-file"
              ? "成果物そのものを表示しています。"
              : "プレビュー（レビュー用の簡易表示）。"}
            {(item.downloadUrl || item.url) && (
              <>
                {kind === "html-file" ? "手元に残すには" : "正式な体裁は"}
                <a
                  href={item.downloadUrl || item.url}
                  download={item.name}
                  className="underline ml-1"
                >
                  ダウンロード
                </a>
                {kind === "html-file" ? "を。" : "で。"}
              </>
            )}
          </p>
        </DialogHeader>
        <div className="flex-1 overflow-auto rounded border bg-white">
          {kind === "html" && (
            <iframe
              title={item.name}
              {...(item.previewHtml ? { srcDoc: item.previewHtml } : { src })}
              sandbox=""
              className="w-full h-[80dvh] border-0"
            />
          )}
          {kind === "html-file" && (
            <div className="flex flex-col h-full">
              {/* 長い解説書 HTML は全画面の方が読みやすい（iPhone 対策も兼ねる・PDF と同じ導線） */}
              <div className="flex-none p-2 text-xs border-b bg-muted/40">
                じっくり読むときは
                <a href={src} target="_blank" rel="noreferrer" className="underline mx-1">
                  新しいタブで開く
                </a>
                が読みやすいです
              </div>
              {/* sandbox="allow-scripts": 図表・タブ切替などの中の JS は動かしつつ、
                  allow-same-origin を付けないことで opaque origin に隔離する（ファイル冒頭コメント参照） */}
              <iframe
                title={item.name}
                src={src}
                sandbox="allow-scripts"
                className="w-full flex-1 min-h-[70dvh] border-0"
              />
            </div>
          )}
          {kind === "pdf" && (
            <div className="flex flex-col h-full">
              {/* iOS Safari は iframe 内 PDF を1ページ目までしか表示できない（スクロール
                  不可の既知挙動）ため、別タブで開く導線を常設する */}
              <div className="flex-none p-2 text-xs border-b bg-muted/40">
                表示が途中で止まる場合は
                <a href={src} target="_blank" rel="noreferrer" className="underline mx-1">
                  新しいタブで開く
                </a>
                （iPhone は全ページ表示にこちらを推奨）
              </div>
              <iframe title={item.name} src={src} className="w-full flex-1 min-h-[60dvh] border-0" />
            </div>
          )}
          {kind === "image" && (
            <div className="flex items-center justify-center p-4">
              <img src={src} alt={item.name} className="max-w-full max-h-[80dvh]" />
            </div>
          )}
          {kind === "markdown" && (
            <div className="prose prose-sm max-w-none p-4">
              {err ? (
                <p className="text-destructive text-sm">読み込めませんでした: {err}</p>
              ) : (
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
              )}
            </div>
          )}
          {kind === "text" && (
            <pre className="whitespace-pre-wrap break-words p-4 text-sm">
              {err ? `読み込めませんでした: ${err}` : text}
            </pre>
          )}
          {(kind === "none" || !kind) && (
            <p className="text-sm text-muted-foreground p-4">
              この形式はプレビューに対応していません。ダウンロードしてご確認ください。
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
